import { callFunction } from '../utils/apiClient';

/**
 * THE UI TRAIL — a record of what this person did, in order, so a problem can be replayed rather than guessed.
 *
 * Owner 2026-09-13: *"add logging to all actions clicked, navigation, error, success, all actions on UI mapped
 * by login session... so we can debug issues easily"*.
 *
 * THE SESSION ID IS THE FEATURE. Events keyed only by user are a pile; keyed by session they are a story with
 * an order, which is what turns "it broke this morning" into one thing to open. It lives in `sessionStorage`,
 * so it is per TAB and dies with it — which is the right granularity: two tabs are two stories, and a new
 * visit tomorrow should not be filed under today.
 *
 * THREE RULES THAT KEEP IT HONEST, because a diagnostic that costs the user anything is not worth having:
 *   - **Batched.** Events queue and flush together, so a page that navigates six times in a second costs one
 *     request rather than six.
 *   - **Fire and forget.** Nothing awaits it, and every failure is swallowed. The trail must never be the
 *     reason an action is slow or an error appears.
 *   - **Never user text.** Actions are stable keys and outcomes are codes. What somebody TYPED is their
 *     content, not our telemetry, and a debugging store is the wrong place for it.
 *
 * It records the UID server-side, never the email. The admin surface resolves one to the other when somebody
 * looks, which answers the same question without an address living in a diagnostic collection.
 */

/** How long events wait for company before being sent. Long enough to batch a burst, short enough to survive. */
const FLUSH_MS = 4000;
/** Mirrors the server's per-request ceiling; a fuller queue flushes early rather than being trimmed. */
const MAX_BATCH = 50;
const SESSION_KEY = 'wt_trail_session';

export interface UiEvent {
  action: string;
  route?: string;
  outcome?: string;
  detail?: string;
  ms?: number;
  at?: number;
}

/**
 * This tab's session id. Minted once and reused; a browser that refuses storage (private mode, blocked site
 * data) still gets an id for the life of the module, so the trail degrades to "this page load" rather than
 * disappearing.
 */
let memorySession = '';
export function trailSessionId(): string {
  if (memorySession) return memorySession;
  const mint = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    const found = window.sessionStorage.getItem(SESSION_KEY);
    memorySession = found || mint();
    if (!found) window.sessionStorage.setItem(SESSION_KEY, memorySession);
  } catch {
    memorySession = mint();
  }
  return memorySession;
}

/** Whether a token is available to send. Imported lazily so the trail pulls no auth weight into the bundle. */
async function signedIn(): Promise<boolean> {
  try {
    const { getAuth } = await import('firebase/auth');
    return !!getAuth().currentUser;
  } catch {
    return false;
  }
}

let queue: UiEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** True when the trail should do nothing at all — the same opt-out shape the analytics service uses. */
function disabled(): boolean {
  return process.env.REACT_APP_DISABLE_UI_TRAIL === 'true' || typeof window === 'undefined';
}

/** Send whatever is queued. Safe to call at any time; a failure drops the batch rather than retrying forever. */
export function flushUiTrail(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  const events = queue;
  queue = [];
  if (!events.length) return;
  /**
   * SIGNED IN OR NOT, BOTH MATTER — and they need different calls.
   *
   * The route is public because the things worth debugging happen before sign-in too: a landing page that
   * will not open, a sign-in that refuses. But the api client's default is to REQUIRE auth and throw before
   * sending, which silently dropped every anonymous batch. So the token travels when there is one (that is
   * what attaches the uid, and a session without it cannot be tied to a person) and the call goes out
   * unauthenticated when there is not, rather than not going at all.
   */
  const payload = {
    sessionId: trailSessionId(),
    events,
    version: process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD_VERSION || undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
  try {
    // Wrapped rather than chained directly: this primitive's whole promise is that it cannot break anything,
    // and that has to hold even when the transport misbehaves and hands back something that is not a promise.
    void Promise.resolve(signedIn())
      .then((authed) => callFunction('logUiEvents', payload, { requireAuth: authed }))
      .catch(() => { /* a lost batch is a lost batch; it must never surface to the person using the product */ });
  } catch { /* nor may a synchronous throw from the transport reach the caller */ }
}

/**
 * Record one thing that happened. Returns immediately — the send is somebody else's problem, on purpose.
 */
export function logUiEvent(action: string, event: Omit<UiEvent, 'action'> = {}): void {
  if (disabled() || !action) return;
  queue.push({
    action,
    route: event.route ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
    outcome: event.outcome,
    detail: event.detail,
    ms: event.ms,
    at: Date.now(),
  });
  // A burst flushes on size rather than waiting out the timer, so a fast sequence is not lost to a reload.
  if (queue.length >= MAX_BATCH) { flushUiTrail(); return; }
  if (!timer) timer = setTimeout(flushUiTrail, FLUSH_MS);
}

/**
 * Flush on the way out. `visibilitychange` rather than `unload`, which browsers no longer fire reliably — and
 * the last events before somebody closes the tab are usually the interesting ones.
 */
export function startUiTrail(): void {
  if (disabled()) return;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushUiTrail(); });
}

/** Test seam: drop anything queued without sending it. */
export function __resetUiTrail(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  queue = [];
  memorySession = '';
}
